import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import useSWR from "swr";
import apiUrl from "../../../../data/api-url";
import { tokenFetcher } from "../../../../data/fetchers";
import { useAuth } from "../../../../hooks/use-auth";
import LoadingBlock from "../../../common/loading/LoadingBlock";
import Table from "../../../common/Table";

export default function UserList() {
  const { t } = useTranslation();
  const { organisationId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  const { data: organisation } = useSWR(
    user && organisationId
      ? [apiUrl("accounts", `/account/${organisationId}`), user.token]
      : null,
    tokenFetcher
  );

  const { data: users } = useSWR(
    [apiUrl("accounts", `/account/${organisationId}/users`), user.token],
    tokenFetcher
  );

  useEffect(() => {
    if (organisation) {
      setLoading(false);
    }
  }, [organisation]);

  const data = useMemo(
    () =>
      users
        ? users.map((user) => ({
            name: user,
            role: user.accountRole,
            action: user,
          }))
        : [],
    [users]
  );

  const columns = useMemo(
    () => [
      {
        Header: t("accountOrganisations.userList.members"),
        accessor: "name",
        Cell: ({ value }) => (
          <div>
            <svg width="67" height="67" className="pr-2 inline">
              <g transform="matrix(0.13958333 0 0 0.13958333 0 0)">
                <path
                  d="M0 32C 0 14.326888 14.326888 0 32 0L32 0L448 0C 465.67313 0 480 14.326888 480 32L480 32L480 448C 480 465.67313 465.67313 480 448 480L448 480L32 480C 14.326888 480 0 465.67313 0 448L0 448L0 32z"
                  stroke="none"
                  fill="#FFFFFF"
                  fillRule="nonzero"
                  fillOpacity="0.09803922"
                />
                <path
                  d="M374.308 240C 374.308 311.691 316.191 369.80798 244.50002 369.80798C 172.80902 369.80798 114.692 311.691 114.692 240C 114.692 168.30899 172.809 110.192 244.5 110.192C 316.19098 110.192 374.308 168.309 374.308 240z"
                  stroke="#FFFFFF"
                  strokeWidth="10.385"
                  fill="#F6F6F6"
                  fillRule="nonzero"
                />
                <path
                  d="M244.5 256.2C 222.873 256.2 179.7 267.05402 179.7 288.6L179.7 288.6L179.7 304.80002L309.3 304.80002L309.3 288.6C 309.3 267.05402 266.12698 256.2 244.49998 256.2M244.49998 240.00002C 262.40097 240.00002 276.9 225.50102 276.9 207.6C 276.9 189.699 262.401 175.20001 244.5 175.20001C 226.599 175.20001 212.1 189.699 212.1 207.6C 212.1 225.501 226.599 240 244.5 240"
                  stroke="none"
                  fill="#35374A"
                  fillRule="nonzero"
                  fillOpacity="0.29803923"
                />
              </g>
            </svg>
            <span>
              {value.givenName} {value.familyName}
            </span>
          </div>
        ),
      },
      {
        Header: t("accountOrganisations.userList.role"),
        accessor: "role",
      },
      {
        Header: "",
        accessor: "action",
        Cell: ({ value }) => {
          if (value.accountRole === "Admin" || users.length === 1) return null;

          return (
            <Link
              to={`/account/organisations/${organisationId}/users/${value.id}/remove`}
            >
              {t("generalActions.remove")}
            </Link>
          );
        },
      },
    ],
    [t, organisationId, users]
  );

  const accountLimitText = () => {
    var text = t("accountOrganisations.userList.paidAccountLimit");
    var usersLabel =
      users.length === 1
        ? t("accountOrganisations.userList.user")
        : t("accountOrganisations.userList.users");
    return text.replace("#USERS#", `${users.length} ${usersLabel}`);
  };

  if (!users || loading) return <LoadingBlock />;

  return (
    <div className="space-y-8">
      <h2>{t("accountOrganisations.userList.title")}</h2>
      <p>{t("accountOrganisations.userList.introText")}</p>
      <p>
        {users.length < organisation?.accountPlan?.maxUsers
          ? accountLimitText()
          : t("accountOrganisations.userList.userLimitReached")}
      </p>
      <div>
        {users.length < organisation?.accountPlan?.maxUsers && (
          <Link
            to={`/account/organisations/${organisationId}/users/invite`}
            className="btn-brand"
          >
            {t("accountOrganisations.userList.inviteUser")}
          </Link>
        )}
      </div>
      <Table columns={columns} data={data} />
    </div>
  );
}
